"use client"

import LinkButton from "components/Buttons/Link"
import Avatar1 from "images/about/hero/Avatar1.png"
import Avatar2 from "images/about/hero/Avatar2.png"
import Avatar3 from "images/about/hero/Avatar3.png"
import StaticImage from "library/StaticImage"
import { css, f, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

export default function Hero() {
	const linkButtonSize = useMedia("medium", "medium", "xl", "medium")
	const mobile = useMedia(false, false, false, true)
	const avatars = [
		{ src: Avatar1, alt: "Avatar 1", id: 1 },
		{ src: Avatar2, alt: "Avatar 2", id: 2 },
		{ src: Avatar3, alt: "Avatar 3", id: 3 },
	]

	return (
		<Wrapper>
			<Inner>
				<Avatars>
					{avatars.map((avatar) => (
						<Avatar
							key={avatar.id}
							src={avatar.src}
							alt={avatar.alt}
							width={mobile ? 48 : 90}
							height={mobile ? 48 : 90}
						/>
					))}
				</Avatars>
				<Text>
					<MobileKickerText>About Us</MobileKickerText>
					<P>
						<KickerText>About Us</KickerText>
						Arlo exists to deliver a new standard of care—patient-centered,
						price-transparent, and and built to drive{" "}
						<GreenText>healthier</GreenText> futures.
					</P>
				</Text>
				<CTALinks aria-label="Calls to action">
					<CTALink href={links.blog} variant="lightAlt" size={linkButtonSize}>
						Read Our Blog
					</CTALink>
					<Line />
					<CTALink
						href={links.careers}
						variant="lightAlt"
						size={linkButtonSize}
					>
						See Open Positions
					</CTALink>
				</CTALinks>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		background: ${colors.offWhite};
		display: grid;
		place-items: center;
		padding: 48px 0;
	`),
	...f.tablet(css`
		padding: 64px 0 66px;
	`),
	...f.mobile(css`
		padding: 24px 0;
	`),
})

const Inner = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green200};
	`),
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 0 auto;
		padding: 115px 120px;
		width: 1312px;
		border-radius: 16px;
		gap: 72px;
	`),
	...f.tablet(css`
		padding: 250px 105px;
		width: 896px;
	`),
	...f.mobile(css`
		align-items: flex-start;
		padding: 20px;
		width: 327px;
		gap: 40px;
	`),
})

const Avatars = styled("div", {
	...f.responsive(css`
		display: flex;
	`),
})

const Avatar = styled(StaticImage, {
	...f.responsive(css`
		width: 90px;
		height: 90px;
		border-radius: 50%;

		&:nth-of-type(1) {
			transform: translateX(0);
			z-index: 2;
		}

		&:nth-of-type(2) {
			transform: translateX(-14px);
			z-index: 1;
		}

		&:nth-of-type(3) {
			transform: translateX(-28px);
			z-index: 0;
		}
	`),
	...f.mobile(css`
		width: 48px;
		height: 48px;

		&:nth-of-type(2) {
			transform: translateX(-10px);
			z-index: 1;
		}

		&:nth-of-type(3) {
			transform: translateX(-20px);
			z-index: 0;
		}
	`),
})

const Text = styled("div", {
	...f.responsive(css`
		width: 1076px;
	`),
	...f.tablet(css`
		width: 683px;
	`),
	...f.mobile(css`
		width: 285px;
		display: flex;
		flex-direction: column;
		gap: 24px;
		margin-bottom: 29px;
	`),
})

const MobileKickerText = styled("p", {
	...f.responsive(css`
		display: none;
		${textStyles.titleS};
		color: ${colors.green300};
	`),
	...f.mobile(css`
		display: block;
	`),
})

const KickerText = styled("span", {
	...f.responsive(css`
		${textStyles.titleS};
		color: ${colors.green300};
		display: inline-block;
		margin-right: 45px;
		transform: translateY(-25px);
	`),
	...f.mobile(css`
		display: none;
	`),
})

const P = styled("p", {
	...f.responsive(css`
		${textStyles.h5};
		color: ${colors.black200};
	`),
	...f.mobile(css`
		${textStyles.custom.h5Mobile};
	`),
})

const greenTextBase = css`
	position: relative;
	color: ${colors.green200};
	transform: skewX(-10deg);
`

const GreenText = styled("span", {
	...f.responsive(css`
		display: inline-block;
		${greenTextBase};
		margin-left: 4px;
	`),
	...f.tablet(css`
		margin-left: 2px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const MobileGreenText = styled("span", {
	...f.responsive(css`
		display: none;
		${greenTextBase};
	`),
	...f.mobile(css`
		display: inline-block;
	`),
})

const Conjunction = styled("span", {
	...f.responsive(css`
		display: inline;
		margin-left: 9px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const MobileConjunction = styled("span", {
	...f.responsive(css`
		display: none;
	`),
	...f.mobile(css`
		display: inline-block;
	`),
})

const CTALinks = styled("div", {
	...f.responsive(css`
		display: flex;
		gap: 24px;
	`),
	...f.mobile(css`
		flex-direction: column;
		gap: 24px;
	`),
})

const Line = styled("div", {
	...f.responsive(css`
		width: 1px;
		height: 11px;
		background: ${colors.green100};
	`),
	...f.mobile(css`
		display: none;
	`),
})

const CTALink = styled(LinkButton, {
	...f.responsive(css`
		/* &:first-of-type {
			margin-right: 24px;
			padding-right: 24px;
			border-right: 1px solid ${colors.green100};
		} */
	`),
	...f.mobile(css`
		/* &:first-of-type {
			margin-right: 0;
			padding-right: 0;
			border-right: none;
		} */
	`),
})
